import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "../constants/api";
import { Button } from "@/components/ui/button";
import {
  LucideUsers,
  LucideEye,
  LucidePencil,
  LucideLogOut,
} from "lucide-react";
import ClassList from "@/components/ClassList";

function PermissionBadge({ permission }) {
  const isEdit = permission === "edit";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-medium leading-none",
        isEdit
          ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
          : "bg-muted text-muted-foreground",
      ].join(" ")}
    >
      {isEdit ? <LucidePencil size={10} /> : <LucideEye size={10} />}
      {isEdit ? "Can edit" : "View only"}
    </span>
  );
}

export default function SharedClasses() {
  const { getToken } = useAuth();

  const [sharedClasses, setSharedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchSharedClasses = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/shared-classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch shared classes");
      setSharedClasses(await res.json());
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching shared classes:", err);
      setFetchError("Could not load shared classes. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLeave = async (item) => {
    if (
      !confirm(
        `Leave "${item.name}"? You will lose access to this shared class.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/shared-classes/${item._id}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setSharedClasses((prev) => prev.filter((c) => c._id !== item._id));
    } catch (err) {
      console.error("Error leaving shared class:", err);
    }
  };

  return (
    <div className="container my-6">
      {/* ── Page header ── */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-foreground leading-tight">
          Shared Classes
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Classes that other teachers have shared with you.
        </p>
      </div>

      {fetchError && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      )}

      <ClassList
        items={sharedClasses}
        loading={loading}
        emptyIcon={LucideUsers}
        emptyTitle="No shared classes yet"
        emptyMessage="When another teacher shares a class with you, it will appear here."
        getLinkPath={(item) => `/shared-classes/${item._id}`}
        renderNameExtra={(item) => <PermissionBadge permission={item.permission} />}
        renderAction={(item) => (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
            onClick={() => handleLeave(item)}
            title="Leave this shared class"
          >
            <LucideLogOut size={14} />
          </Button>
        )}
        extraMetadata={(item) =>
          item.sharedBy?.name ? [`Shared by ${item.sharedBy.name}`] : []
        }
      />
    </div>
  );
}
